import { useEffect, useState } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { sendGTMEvent } from '@next/third-parties/google';
import { useRouter } from 'next/router';
import { FormProvider, useForm } from 'react-hook-form';

import { ThankYou } from '@/components/common/CtaFormThankYou';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { StyledParagraph } from '@/components/common/MarkUp';
import { skeleton } from '@/components/common/skeleton';
import { getLegalConsentFromMetadata } from '@/components/forms/HubspotFormNew/hubspotUtils';
import { getCookie } from '@/lib/utils';

import type { FormColourTheme } from '@/components/forms/HubspotFormNew/HubspotFormTheme';
import type { FormStyleVariant } from '@/components/forms/styles';
import type { ReactNode } from 'react';

export const Loading = styled.div`
  ${skeleton(
    [
      [150, 20],
      [250, 60],
    ],
    150,
  )}
`;

export interface HubspotFormNewProps {
  formData: any;
  onSubmit?: () => void;
  endpoint?: string;
  variant?: FormStyleVariant;
  colourTheme?: FormColourTheme;
  children: ReactNode;
}

export const HubspotFormNew = ({
  formData,
  onSubmit,
  endpoint,
  variant = 'original',
  colourTheme = 'light',
  children,
}: HubspotFormNewProps) => {
  const router = useRouter();
  const [hasHubspotError, setHasHubspotError] = useState(false);
  const [utm, setUTM] = useState(null);

  const methods = useForm();
  const { guid, metaData } = formData;

  const legalConsent = getLegalConsentFromMetadata(metaData);

  const handleOnSubmit = async (data, event) => {
    event.preventDefault();
    setHasHubspotError(false);
    // TODO: Does this need 'objectTypeId'?
    // https://legacydocs.hubspot.com/docs/methods/forms/submit_form
    let fields = Object.keys(data).map((key) => ({
      name: key,
      value: data[key],
    }));
    if (utm) fields = [...fields, ...utm];
    const response = await fetch(endpoint ?? `/api/form/${guid}`, {
      body: JSON.stringify({
        fields,
        formId: guid,
        context: {
          pageUri: window.location.href,
          pageName: document.title,
        },
        legalConsent: legalConsent
          ? {
              legitimateInterest: {
                value: true,
                subscriptionTypeId:
                  legalConsent.legitimateInterestSubscriptionTypes[0],
                // Legal basis on the Hubspot side is one of 3 options, whereas the form API only accepts 2 different values.
                // My guess is that a "Lead" is a better default than a "Customer" here.
                legalBasis:
                  legalConsent.legitimateInterestLegalBasis ===
                  'LEGITIMATE_INTEREST_CLIENT'
                    ? 'CUSTOMER'
                    : 'LEAD',
                text: legalConsent.privacyPolicyText,
              },
            }
          : null,
      }),
      method: 'POST',
    });
    const result = await response.json();

    if (result.error || result.status === 'error' || result.errors?.length) {
      setHasHubspotError(true);
      return;
    }

    sendGTMEvent({
      event: 'hubspot-form-success',
      'hs-form-guid': guid,
    });

    if (onSubmit) {
      onSubmit();
    }
  };

  const handleOnError = (_, event) => {
    event.preventDefault();
  };

  useEffect(() => {
    if (
      router.isReady &&
      Object.keys(router.query).filter((key) => /utm/.test(key)).length > 0
    ) {
      const fields = Object.keys(router.query).map((key) => ({
        name: key,
        value: router.query[key],
      }));
      setUTM(fields);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady]);

  useEffect(() => {
    const utmCookieString = getCookie('utmParams');
    if (utmCookieString) {
      const utmElsArray = utmCookieString
        .split('&')
        .map((utmEl) => utmEl.split('='));
      utmElsArray.forEach(([utmKey, utmVal]) =>
        methods.setValue(utmKey, utmVal),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (!hasHubspotError && methods.formState.isSubmitSuccessful) {
    return <ThankYou variant={variant} colourTheme={colourTheme} />;
  }

  return (
    <FormProvider {...methods}>
      <FormWrapper
        variant={variant}
        method="post"
        action={`/api/form/submit/${guid}`}
        onSubmit={methods.handleSubmit(handleOnSubmit, handleOnError)}
        noValidate
      >
        {hasHubspotError ? (
          <ErrorAlert>
            <StyledParagraph size="body-md">
              There was an error submitting the form, please try again or come
              back later.
            </StyledParagraph>
          </ErrorAlert>
        ) : null}
        {children}
      </FormWrapper>
    </FormProvider>
  );
};

const FormWrapper = styled.form<{ variant?: FormStyleVariant }>`
  ${({ variant }) =>
    variant === 'fresh' &&
    css`
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      height: 100%;
    `}
`;
